import React from 'react';
import {
  SearchInput,
  SearchDetails,
  Facets,
  SortOn,
} from '@plone/volto/components/manage/Blocks/Search/components';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import loupeSVG from '@plone/volto/icons/zoom.svg';
import { Button, Grid, Input } from 'semantic-ui-react';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  sort: {
    id: 'Sorting',
    defaultMessage: 'Sorting',
  },
});

const FacetSearchInput = (props) => {
  const {
    data,
    searchText,
    setSearchText,
    isLive,
    isEditMode,
    onTriggerSearch,
  } = props;
  const inputRef = React.useRef(null);
  const intl = useIntl();
  const inputId = `${props.id}-searchtext`;

  React.useEffect(() => {
    if (!isEditMode) inputRef.current?.focus();
  }, [isEditMode]);

  return (
    <div className="dgbbw-facet-search">
      <div className="dgbbw-facet-search-field">
        <Input
          id={inputId}
          ref={inputRef}
          value={searchText}
          placeholder={
            data.searchInputPrompt || intl.formatMessage(messages.search)
          }
          fluid
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onTriggerSearch(searchText);
            }
          }}
          onChange={(event, { value }) => {
            setSearchText(value);
            if (isLive) onTriggerSearch(value);
          }}
          aria-label={intl.formatMessage(messages.search)}
        />
      </div>
      <Button
        basic
        icon
        type="button"
        className="dgbbw-facet-search-submit"
        aria-label={intl.formatMessage(messages.search)}
        onClick={() => onTriggerSearch(searchText)}
      >
        <Icon name={loupeSVG} />
      </Button>
    </div>
  );
};

const TopSideFacets = (props) => {
  const {
    children,
    data,
    totalItems,
    facets,
    setFacets,
    setSortOn,
    setSortOrder,
    sortOn,
    sortOrder,
    onTriggerSearch,
    searchedText,
    isEditMode,
    querystring = {},
  } = props;
  const isLive = !data.showSearchButton;
  const showSearchInput = Object.keys(data).includes('showSearchInput')
    ? data.showSearchInput
    : true;
  const intl = useIntl();
  const facetCount = data.facets?.length || 1;
  const colWidth = facetCount < 5 ? 12 / facetCount : 4;
  const configuredFacetFields =
    data.facets?.map((facet) => facet.field?.value || facet.field) || [];
  const isSeminarSearch = [
    'programmkategorie',
    'zielgruppen',
    'seminar_kategorie_intern',
    'referenten',
    'start_date',
  ].every((field) => configuredFacetFields.includes(field));
  const showFacetSearch = showSearchInput || isSeminarSearch;

  const FacetWrapper = ({ children: facet }) => (
    <Grid.Column mobile={12} tablet={colWidth} computer={colWidth}>
      {facet}
    </Grid.Column>
  );

  return (
    <div className="searchBlock-container">
      {data.headline && <h2 className="headline">{data.headline}</h2>}

      <Grid className="searchBlock-facets" stackable>
        <Grid.Row>
          <Grid.Column>
            {!data.facets?.length && showSearchInput && (
              <div className="search-wrapper">
                <SearchInput {...props} isLive={isLive} />
              </div>
            )}

            {((showSearchInput && !isSeminarSearch) ||
              searchedText ||
              data.sortOnOptions?.length > 0) && (
              <div className="search-details-sort dgbbw-search-details-sort">
                <SearchDetails text={searchedText} total={totalItems} as="h5" />
                {data.sortOnOptions?.length > 0 && (
                  <div className="sort-on-wrapper">
                    <span className="sort-label">
                      {intl.formatMessage(messages.sort)}
                    </span>
                    <SortOn
                      data={data}
                      querystring={querystring}
                      isEditMode={isEditMode}
                      sortOn={sortOn}
                      sortOrder={sortOrder}
                      setSortOn={(nextSortOn) => {
                        flushSync(() => {
                          setSortOn(nextSortOn);
                          onTriggerSearch(
                            searchedText || '',
                            facets,
                            nextSortOn,
                          );
                        });
                      }}
                      setSortOrder={(nextSortOrder) => {
                        flushSync(() => {
                          setSortOrder(nextSortOrder);
                          onTriggerSearch(
                            searchedText || '',
                            facets,
                            sortOn,
                            nextSortOrder,
                          );
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {data.facets?.length > 0 && (
              <div className="facets">
                {data.facetsTitle && <h3>{data.facetsTitle}</h3>}
                <Grid verticalAlign="bottom" columns={12}>
                  {showFacetSearch && (
                    <Grid.Column
                      className="dgbbw-search-facet-column"
                      mobile={12}
                      tablet={colWidth}
                      computer={colWidth}
                    >
                      <FacetSearchInput {...props} isLive={isLive} />
                    </Grid.Column>
                  )}
                  <Facets
                    data={data}
                    querystring={querystring}
                    facets={facets}
                    setFacets={(nextFacets) => {
                      flushSync(() => {
                        setFacets(nextFacets);
                        onTriggerSearch(searchedText || '', nextFacets);
                      });
                    }}
                    facetWrapper={FacetWrapper}
                  />
                </Grid>
              </div>
            )}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>{children}</Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default TopSideFacets;
